/**
 * Which profile fields a registration asks for, and how strictly
 * (docs/superpowers/specs/2026-10-19-registration-forms-design.md, D1).
 * First name, last name and email are always required and are not listed.
 * Stored as jsonb on registration_presets / content / tickets; the database
 * checks the shape with registration_fields_valid().
 */
export type FieldState = 'required' | 'optional' | 'hidden';

export const CONFIGURABLE_FIELDS = [
  'patronymic', 'phone', 'date_of_birth', 'country', 'region', 'city', 'address',
  'postal_code', 'healthcare_role', 'specialty', 'education', 'workplace', 'position',
] as const;

export type ConfigurableField = (typeof CONFIGURABLE_FIELDS)[number];

/** A field missing from the object is hidden (not asked). */
export type FieldStates = Partial<Record<ConfigurableField, FieldState>>;

const FIELD_SET = new Set<string>(CONFIGURABLE_FIELDS);
const STATE_SET = new Set<string>(['required', 'optional', 'hidden']);

/** The rule every event had before registration forms: content.registration_fields IS NULL. */
export const TODAY_RULE: FieldStates = Object.fromEntries(
  CONFIGURABLE_FIELDS.map(f => [f, f === 'postal_code' || f === 'workplace' ? 'optional' : 'required'])
) as FieldStates;

/** Required on a БПР certificate ticket whatever the form says (D6) – the МОЗ workbook needs them. */
export const BPR_LOCK = ['patronymic', 'date_of_birth', 'education', 'workplace', 'position'] as const;

export function stateOf(states: FieldStates, field: ConfigurableField): FieldState {
  return states[field] ?? 'hidden';
}

export function parseFieldStates(raw: unknown): FieldStates | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: FieldStates = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!FIELD_SET.has(key) || typeof value !== 'string' || !STATE_SET.has(value)) return null;
    out[key as ConfigurableField] = value as FieldState;
  }
  return out;
}

export function withBprLock(states: FieldStates): FieldStates {
  const locked: FieldStates = { ...states };
  for (const field of BPR_LOCK) locked[field] = 'required';
  return locked;
}
